// Network + session layer for the chat assistant. Talks to the backend that is
// reverse-proxied at /api (see vue.config.js devServer proxy, and the single-
// unit deploy where server/ serves the built dist). UI orchestration (rendering
// bubbles, executing validated actions) lives in ChatWidget.vue.

import { cartSnapshot } from './cart'

const SESSION_KEY = 'xs-chat-session'
const HISTORY_KEY = 'xs-chat-history'
const API_BASE = '/api'

// AbortSignal.timeout() rejects with a DOMException named 'TimeoutError'
// (some environments still surface a plain 'AbortError') — treat either as
// a timeout rather than lumping it in with a generic network failure.
function isTimeoutError(e) {
  return !!e && (e.name === 'TimeoutError' || e.name === 'AbortError')
}

// --- session id -------------------------------------------------------------
export function getSessionId() {
  let id = null
  try {
    id = sessionStorage.getItem(SESSION_KEY)
  } catch (e) {
    id = null
  }
  if (!id) {
    id = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'xs-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2)
    try { sessionStorage.setItem(SESSION_KEY, id) } catch (e) { /* ignore */ }
  }
  return id
}

// --- history persistence (survives route changes within a tab) --------------
export function loadHistory() {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

export function saveHistory(messages) {
  try {
    // Cap what we persist so a very long conversation can't blow the
    // sessionStorage quota (silently swallowed below) or keep an unbounded
    // number of product/DIY cards mounted after a history reload.
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify((messages || []).slice(-60)))
  } catch (e) { /* quota / disabled — non-fatal */ }
}

export function clearHistory() {
  try {
    sessionStorage.removeItem(HISTORY_KEY)
    // Fresh conversation → fresh session id so server-side context resets too.
    sessionStorage.removeItem(SESSION_KEY)
  } catch (e) { /* ignore */ }
}

// --- health -----------------------------------------------------------------
// Never throws: returns { ok, hasKey, model, mock }. In dev without the server
// up, the fetch rejects and we report offline instead of blowing up on mount.
// `mock` is true when the backend is serving canned/demo replies (no API key
// configured but DEEPSEEK_MOCK=1) — the widget shows an honest demo-mode note
// so canned answers aren't mistaken for a bug.
export async function checkHealth() {
  try {
    const res = await fetch(`${API_BASE}/health`, { method: 'GET', signal: AbortSignal.timeout(5000) })
    if (!res.ok) return { ok: false, hasKey: false, model: '', mock: false }
    const data = await res.json()
    return {
      ok: !!data.ok,
      hasKey: !!data.hasKey,
      model: data.model || '',
      mock: !!data.mock
    }
  } catch (e) {
    // Includes AbortError from the timeout above — treat a stalled health
    // probe the same as an unreachable backend.
    return { ok: false, hasKey: false, model: '', mock: false }
  }
}

// --- chat -------------------------------------------------------------------
// messages: [{ role:'user'|'assistant', content }] already windowed by caller.
// `cart` is the caller's inquiry-cart snapshot; when omitted we derive it from
// the shared cart store so the assistant can always answer about the cart.
// Returns { reply, actions, meta }. Throws an Error with `.offline` / `.rateLimit`
// flags so the widget can pick the right UI state.
export async function sendChat({ messages, locale, page, cart }) {
  const cartPayload = Array.isArray(cart) ? cart.slice(0, 30) : cartSnapshot()
  let res
  try {
    res = await fetch(`${API_BASE}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: getSessionId(),
        locale,
        page,
        messages: (messages || []).slice(-12),
        cart: cartPayload
      }),
      signal: AbortSignal.timeout(30000)
    })
  } catch (e) {
    if (isTimeoutError(e)) {
      const err = new Error('timeout')
      err.timeout = true
      throw err
    }
    const err = new Error('network')
    err.offline = true
    throw err
  }

  if (res.status === 503) {
    const err = new Error('assistant_offline')
    err.offline = true
    throw err
  }
  if (res.status === 429) {
    const err = new Error('rate_limited')
    err.rateLimit = true
    err.retryAfter = Number(res.headers.get('Retry-After')) || 60
    throw err
  }
  if (!res.ok) {
    throw new Error('chat_failed_' + res.status)
  }

  const data = await res.json()
  return {
    reply: typeof data.reply === 'string' ? data.reply : '',
    actions: Array.isArray(data.actions) ? data.actions : [],
    meta: data.meta && typeof data.meta === 'object' ? data.meta : {}
  }
}

// --- newsletter subscribe -----------------------------------------------------
// Persists the email server-side (data/subscribers.json, deduped). Returns
// { ok: true }; throws on invalid email / network failure.
export async function postSubscribe({ email, locale }) {
  let res
  try {
    res = await fetch(`${API_BASE}/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, locale }),
      signal: AbortSignal.timeout(10000)
    })
  } catch (e) {
    const err = new Error(isTimeoutError(e) ? 'timeout' : 'network')
    err.offline = !isTimeoutError(e)
    err.timeout = isTimeoutError(e)
    throw err
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.ok) {
    const err = new Error(data.error || 'subscribe_failed')
    err.status = res.status
    throw err
  }
  return data
}

// --- inquiry (quote form submit) --------------------------------------------
// Returns { ok, inquiryId, autoReply }. Throws on failure (validation or network).
export async function postInquiry(payload) {
  let res
  try {
    res = await fetch(`${API_BASE}/inquiry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(15000)
    })
  } catch (e) {
    if (isTimeoutError(e)) {
      const err = new Error('timeout')
      err.timeout = true
      throw err
    }
    const err = new Error('network')
    err.offline = true
    throw err
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok || !data.ok) {
    const err = new Error(data.error || 'inquiry_failed')
    err.status = res.status
    throw err
  }
  return data
}
